// QR4 + Mode-UX closeout — per-USER cached feature-flag map. The cache is
// scoped to the authenticated user_id and cleared on logout/account switch so
// flag state can NEVER leak between accounts in the same SPA session.
//
// Backend shape (growth_ops.my_feature_flags):
//   GET /api/feature-flags/me  →  { "<flag_name>": bool, ... }
//
// The hook returns:
//   { ready: bool, on: bool, flags: Record<string, bool> }
//
// `ready` toggles to true once the first request resolves (or fails). Until
// then, callers should treat `on` as false and render nothing — never flash
// a flagged surface before the gate resolves.
import { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';

let _cache = null;
let _inflight = null;
let _cacheUser; // undefined = not yet synced with AuthContext
let _generation = 0; // bumped on account switch/reset — stale responses discarded
const _subscribers = new Set();

const notifySubscribers = () => {
  _subscribers.forEach((fn) => { try { fn(); } catch { /* isolated subscriber error */ } });
};

const fetchFlags = (force = false) => {
  if (force) { _cache = null; _inflight = null; }
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  // Generation binding (release-safety P0): a response that started under a
  // previous account/generation must NEVER populate the cache — otherwise an
  // in-flight User-A request resolving after User-B logs in leaks A's flags.
  const gen = _generation;
  _inflight = api.get('/api/feature-flags/me')
    .then(d => {
      if (gen !== _generation) return _cache || {};
      _cache = (d && d.flags) || {};
      return _cache;
    })
    .catch(() => {
      if (gen !== _generation) return _cache || {};
      _cache = {};
      return _cache;
    })
    .finally(() => { if (gen === _generation) _inflight = null; });
  return _inflight;
};

// Called by AuthContext whenever the authenticated user changes (login,
// logout, account switch). First sync adopts the session owner; any CHANGE
// wipes the cache, aborts in-flight resolution and re-resolves every hook.
export const syncFlagCacheUser = (userId) => {
  const uid = userId || null;
  if (_cacheUser === undefined) { _cacheUser = uid; return; }
  if (_cacheUser === uid) return;
  _cacheUser = uid;
  _generation += 1;
  _cache = null;
  _inflight = null;
  notifySubscribers();
};

export const useFeatureFlag = (flagName) => {
  const [state, setState] = useState({ ready: !!_cache, on: !!(_cache && _cache[flagName]), flags: _cache || {} });
  useEffect(() => {
    let cancelled = false;
    const apply = (flags) => {
      if (cancelled) return;
      setState({ ready: true, on: !!flags[flagName], flags });
    };
    fetchFlags().then(apply);
    // Subscribe so an admin flag flip (resetFeatureFlagCache) or an account
    // switch (syncFlagCacheUser) re-fetches and updates THIS mounted hook.
    const onInvalidate = () => fetchFlags(true).then(apply);
    _subscribers.add(onInvalidate);
    return () => { cancelled = true; _subscribers.delete(onInvalidate); };
  }, [flagName]);
  return state;
};

// Map-only variant for surfaces that need the whole resolved flag set.
export const useFeatureFlags = () => useFeatureFlag(null).flags;

// Force-refresh + notify every mounted useFeatureFlag hook to re-resolve.
export const resetFeatureFlagCache = () => {
  _generation += 1;
  _cache = null;
  _inflight = null;
  notifySubscribers();
};

export default useFeatureFlag;
